<template>
  <div class="material-check-page frame-page excel-split-page" :class="{ 'is-loading': loading }">
    <div class="page-intro">
      <p>
        上传表格（.xls / .xlsx / .mht / .mhtml），按指定列拆分，支持筛选与列类型修正。
      </p>
    </div>

    <input ref="fileInput" type="file" hidden accept=".xls,.xlsx,.mht,.mhtml" @change="onFileSelected" />

    <!-- 1 上传 -->
    <section class="panel">
      <div class="panel-title panel-title-actions">
        <div class="panel-title-left">
          <span class="step">1</span>
          <span>上传表格</span>
          <span v-if="displayName" class="tag tag-done">已上传</span>
          <span v-else class="tag tag-muted">待选择</span>
        </div>
        <div class="panel-title-right">
          <Button size="s" class="drop-btn-clear" @click="doClear">清除</Button>
          <Button size="s" :disabled="!displayName" @click="reloadCurrent">重新加载</Button>
          <Button size="s" color="primary" @click="pickFile">浏览</Button>
        </div>
      </div>
      <div
        class="drop-zone"
        :class="{ 'is-empty': !displayName, 'is-dragover': dragOver, 'is-filled': !!displayName }"
        @click="pickFile"
        @dragenter.prevent="dragOver = true"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="onDrop"
      >
        <div class="drop-zone-body drop-zone-body-compact">
          <div class="drop-icon" :class="{ 'is-uploaded': !!displayName }">
            <span v-if="displayName" class="drop-check">✓</span>
            <span v-else>Excel</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !displayName }">
              {{ displayName || '选择或拖拽表格文件' }}
            </div>
            <div class="drop-hint">
              <template v-if="displayName">{{ sheetName }} · {{ rowCount }} 行 · {{ columns.length }} 列</template>
              <template v-else>支持 .xls / .xlsx / .mht / .mhtml</template>
            </div>
          </div>
        </div>
      </div>
      <div v-if="sheetNames.length" class="sheet-row">
        <span class="field-label">工作表</span>
        <Select v-model="sheetName" :datas="sheetOptions" filterable style="width: 260px" />
      </div>
    </section>

    <template v-if="columns.length">
      <div class="two-col">
        <!-- 2 按列拆分 -->
        <section class="panel">
          <div class="panel-title panel-title-actions">
            <div class="panel-title-left">
              <span class="step">2</span>
              <span>按列拆分</span>
              <span v-if="splitColumns.length" class="tag tag-done">{{ splitColumns.length }} 列</span>
              <span v-else class="tag tag-muted">待添加</span>
            </div>
          </div>
          <p class="section-hint">可多列组合：文件夹 → 多文件；工作簿 → 多工作表</p>
          <div class="inline-row">
            <Select
              v-model="pendingSplitCol"
              :datas="availableSplitOptions"
              filterable
              placeholder="选择列"
              style="flex: 1"
            />
            <Button size="s" color="primary" :disabled="!pendingSplitCol" @click="addSplitCol">添加</Button>
          </div>
          <div v-if="!splitColumns.length" class="empty-hint">尚未添加拆分依据列</div>
          <ul v-else class="item-list">
            <li v-for="col in splitColumns" :key="col">
              <div>
                <strong>{{ col }}</strong>
                <span class="badge">拆分依据</span>
              </div>
              <Button size="s" class="drop-btn-clear" @click="removeSplitCol(col)">移除</Button>
            </li>
          </ul>
        </section>

        <!-- 3 筛选 -->
        <section class="panel">
          <div class="panel-title panel-title-actions">
            <div class="panel-title-left">
              <span class="step">3</span>
              <span>筛选条件</span>
              <span v-if="filters.length" class="tag tag-done">{{ filters.length }} 条</span>
              <span v-else class="tag tag-muted">可选</span>
            </div>
            <div class="panel-title-right">
              <Button size="s" :disabled="!filters.length" @click="applyPreview">预览</Button>
            </div>
          </div>
          <p class="section-hint">添加多层查询条件（依列类型显示）</p>
          <div class="inline-row">
            <Select
              v-model="pendingFilterCol"
              :datas="columnOptions"
              filterable
              placeholder="选择列"
              style="flex: 1"
            />
            <Button size="s" color="primary" :disabled="!pendingFilterCol" @click="addFilter">添加</Button>
          </div>
          <div v-if="!filters.length" class="empty-hint">不筛选则对全部数据拆分</div>
          <ul v-else class="item-list">
            <li v-for="(f, idx) in filters" :key="idx" class="filter-item">
              <div class="filter-grid">
                <Select v-model="f.column" :datas="columnOptions" filterable @change="() => syncKind(f)" />
                <span class="op">{{ kindLabel(f.kind) }}</span>
                <template v-if="f.kind === 'date'">
                  <input v-model="f.start" class="inp" type="date" />
                  <span class="op">至</span>
                  <input v-model="f.end" class="inp" type="date" />
                </template>
                <template v-else-if="f.kind === 'number'">
                  <input v-model="f.min" class="inp" type="number" placeholder="最小" />
                  <span class="op">至</span>
                  <input v-model="f.max" class="inp" type="number" placeholder="最大" />
                </template>
                <template v-else>
                  <span class="op">包含</span>
                  <input v-model="f.text" class="inp grow" type="text" placeholder="关键字" />
                </template>
              </div>
              <Button size="s" class="drop-btn-clear" @click="filters.splice(idx, 1)">移除</Button>
            </li>
          </ul>
        </section>
      </div>

      <!-- 4 列类型 -->
      <section class="panel">
        <div class="panel-title panel-title-actions">
          <div class="panel-title-left">
            <span class="step">4</span>
            <span>列类型</span>
            <span class="tag tag-muted">影响筛选与导出</span>
          </div>
          <div class="panel-title-right kind-tools">
            <Button size="s" @click="resetAllKinds">全部恢复自动判断</Button>
            <button
              v-for="tab in kindTabs"
              :key="tab.key"
              type="button"
              class="tab-btn"
              :class="{ active: kindFilter === tab.key }"
              @click="kindFilter = tab.key"
            >
              {{ tab.title }}
            </button>
          </div>
        </div>
        <div class="kind-table-wrap">
          <table class="kind-table">
            <thead>
              <tr>
                <th style="width: 42%">列名</th>
                <th style="width: 22%">自动判断</th>
                <th style="width: 36%">修正为</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="col in filteredKindColumns" :key="col">
                <td>{{ col }}</td>
                <td><span class="auto-tag">{{ kindLabel(autoKinds[col]) }}</span></td>
                <td>
                  <select
                    class="kind-select"
                    :value="columnKinds[col] || 'text'"
                    @change="onKindChange(col, $event.target.value)"
                  >
                    <option value="text">文本</option>
                    <option value="date">日期</option>
                    <option value="number">数字</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 5 输出 -->
      <section class="panel">
        <div class="panel-title panel-title-actions">
          <div class="panel-title-left">
            <span class="step">5</span>
            <span>生成结果</span>
            <span v-if="lastResult" class="tag tag-done">已完成</span>
            <span v-else class="tag tag-muted">待拆分</span>
          </div>
          <div class="panel-title-right">
            <Button size="s" class="drop-btn-clear" :disabled="!lastResult" @click="clearResult">清除结果</Button>
            <Button size="s" :disabled="!lastResult" @click="openResult">打开输出</Button>
            <Button size="s" color="primary" :disabled="!canRun" @click="doSplit">开始拆分</Button>
          </div>
        </div>

        <div class="output-mode-row">
          <span class="field-label">输出方式</span>
          <label class="radio">
            <input v-model="outputMode" type="radio" value="files" />
            <span>多个文件（zip）</span>
          </label>
          <label class="radio">
            <input v-model="outputMode" type="radio" value="workbook" />
            <span>单个文件（多工作表）</span>
          </label>
        </div>

        <div class="drop-zone result-zone" :class="{ 'is-empty': !lastResult, 'is-filled': !!lastResult }">
          <div class="drop-zone-body drop-zone-body-compact">
            <div class="drop-icon" :class="{ 'is-uploaded': !!lastResult }">
              <span v-if="lastResult" class="drop-check">✓</span>
              <span v-else>结果</span>
            </div>
            <div class="drop-text">
              <div class="drop-title" :class="{ placeholder: !lastResult }">
                <template v-if="lastResult">{{ lastResult.filename }}</template>
                <template v-else>添加拆分列后点击「开始拆分」</template>
              </div>
              <div class="drop-hint">
                <template v-if="lastResult">
                  共拆出 {{ lastResult.count }} 组 · 数据 {{ lastResult.rowCount }} 行
                  <a class="dl-link" :href="downloadUrl" target="_blank" rel="noopener">下载</a>
                </template>
                <template v-else>拆分后将尝试用本机 Excel 打开</template>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { message } from 'heyui';
import { excelSplitApi as api } from '@/api/excelSplit';
import '@/views/material-check/material-check.less';

const loading = ref(false);
const dragOver = ref(false);
const fileInput = ref(null);
const switchingSheet = ref(false);

const displayName = ref('');
const sheetNames = ref([]);
const sheetName = ref('');
const columns = ref([]);
const columnKinds = ref({});
const autoKinds = ref({});
const rowCount = ref(0);
const splitColumns = ref([]);
const filters = ref([]);
const outputMode = ref('workbook');
const lastResult = ref(null);

const pendingSplitCol = ref('');
const pendingFilterCol = ref('');
const kindFilter = ref('all');

const sheetOptions = computed(() => sheetNames.value.map((s) => ({ key: s, title: s })));
const columnOptions = computed(() => columns.value.map((c) => ({ key: c, title: c })));
const availableSplitOptions = computed(() =>
  columns.value.filter((c) => !splitColumns.value.includes(c)).map((c) => ({ key: c, title: c }))
);
const kindTabs = [
  { key: 'all', title: '全部' },
  { key: 'text', title: '文本' },
  { key: 'date', title: '日期' },
  { key: 'number', title: '数字' },
];
const filteredKindColumns = computed(() => {
  if (kindFilter.value === 'all') return columns.value;
  return columns.value.filter((c) => (columnKinds.value[c] || autoKinds.value[c]) === kindFilter.value);
});
const canRun = computed(() => !!displayName.value && splitColumns.value.length > 0);
const downloadUrl = computed(() => api.downloadUrl());

function kindLabel(kind) {
  return { text: '文本', date: '日期', number: '数字' }[kind] || '文本';
}

function pickFile() {
  fileInput.value && fileInput.value.click();
}

function onFileSelected(e) {
  const file = e.target.files && e.target.files[0];
  e.target.value = '';
  if (file) upload(file);
}

function onDrop(e) {
  dragOver.value = false;
  const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
  if (file) upload(file);
}

function applyState(data) {
  switchingSheet.value = true;
  displayName.value = data.displayName || '';
  sheetNames.value = data.sheetNames || [];
  sheetName.value = data.sheetName || '';
  columns.value = data.columns || [];
  columnKinds.value = { ...(data.columnKinds || {}) };
  autoKinds.value = { ...(data.autoKinds || data.columnKinds || {}) };
  rowCount.value = data.rowCount || 0;
  lastResult.value = data.lastResult || null;
  splitColumns.value = splitColumns.value.filter((c) => columns.value.includes(c));
  pendingSplitCol.value = '';
  pendingFilterCol.value = '';
  setTimeout(() => {
    switchingSheet.value = false;
  }, 0);
}

async function withLoading(fn) {
  loading.value = true;
  try {
    await fn();
  } finally {
    loading.value = false;
  }
}

async function upload(file) {
  const name = (file.name || '').toLowerCase();
  const okExt = ['.xls', '.xlsx', '.mht', '.mhtml'].some((ext) => name.endsWith(ext));
  if (!okExt) {
    message.error('请选择 .xls / .xlsx / .mht / .mhtml 文件');
    return;
  }
  await withLoading(async () => {
    const resp = await api.upload(file);
    if (!resp.ok) {
      message.error(resp.error || '上传失败');
      return;
    }
    splitColumns.value = [];
    filters.value = [];
    applyState(resp);
    message.success('上传成功');
  });
}

async function reloadCurrent() {
  if (!sheetName.value) return;
  await withLoading(async () => {
    const resp = await api.sheet(sheetName.value);
    if (!resp.ok) {
      message.error(resp.error || '重新加载失败');
      return;
    }
    applyState(resp);
    message.success('已重新加载');
  });
}

async function onSheetChange() {
  if (!sheetName.value || switchingSheet.value) return;
  await withLoading(async () => {
    switchingSheet.value = true;
    try {
      const resp = await api.sheet(sheetName.value);
      if (!resp.ok) {
        message.error(resp.error || '切换工作表失败');
        return;
      }
      splitColumns.value = [];
      filters.value = [];
      applyState(resp);
    } finally {
      switchingSheet.value = false;
    }
  });
}

watch(sheetName, (val, oldVal) => {
  if (!val || val === oldVal || !displayName.value) return;
  onSheetChange();
});

function addSplitCol() {
  if (!pendingSplitCol.value) return;
  if (!splitColumns.value.includes(pendingSplitCol.value)) {
    splitColumns.value.push(pendingSplitCol.value);
  }
  pendingSplitCol.value = '';
}

function removeSplitCol(col) {
  splitColumns.value = splitColumns.value.filter((c) => c !== col);
}

function addFilter() {
  const col = pendingFilterCol.value || columns.value[0] || '';
  if (!col) return;
  filters.value.push({
    column: col,
    kind: columnKinds.value[col] || 'text',
    text: '',
    start: '',
    end: '',
    min: '',
    max: '',
  });
  pendingFilterCol.value = '';
}

function syncKind(f) {
  f.kind = columnKinds.value[f.column] || 'text';
}

function buildFilters() {
  return filters.value
    .filter((f) => f.column)
    .map((f) => ({
      column: f.column,
      kind: f.kind || columnKinds.value[f.column] || 'text',
      text: f.text,
      start: f.start || null,
      end: f.end || null,
      min: f.min === '' ? null : f.min,
      max: f.max === '' ? null : f.max,
    }));
}

async function applyPreview() {
  await withLoading(async () => {
    const resp = await api.preview(buildFilters());
    if (!resp.ok) {
      message.error(resp.error || '预览失败');
      return;
    }
    rowCount.value = resp.rowCount || 0;
    message.success(`筛选后 ${rowCount.value} 行`);
  });
}

function clearResult() {
  lastResult.value = null;
  message.success('已清除生成结果');
}

async function onKindChange(col, kind) {
  columnKinds.value = { ...columnKinds.value, [col]: kind };
  filters.value.forEach((f) => {
    if (f.column === col) f.kind = kind;
  });
  await api.setKinds({ [col]: kind });
}

async function resetAllKinds() {
  const resp = await api.resetKinds();
  if (resp.ok) {
    applyState(resp);
    message.success('已恢复自动判断');
  }
}

async function doSplit() {
  if (!canRun.value) {
    message.warn('请先添加拆分列');
    return;
  }
  await withLoading(async () => {
    const resp = await api.run({
      splitColumns: splitColumns.value,
      filters: buildFilters(),
      columnKinds: columnKinds.value,
      mode: outputMode.value,
      openAfter: true,
    });
    if (!resp.ok) {
      message.error(resp.error || '拆分失败');
      return;
    }
    lastResult.value = resp;
    message.success(`拆分完成，共 ${resp.count} 组`);
  });
}

function openResult() {
  if (!lastResult.value) return;
  window.open(downloadUrl.value, '_blank');
}

async function doClear() {
  await withLoading(async () => {
    await api.reset();
    displayName.value = '';
    sheetNames.value = [];
    sheetName.value = '';
    columns.value = [];
    columnKinds.value = {};
    autoKinds.value = {};
    rowCount.value = 0;
    splitColumns.value = [];
    filters.value = [];
    lastResult.value = null;
  });
}

onMounted(async () => {
  const resp = await api.state();
  if (resp.ok) applyState(resp);
});
</script>

<style lang="less" scoped>
.excel-split-page {
  max-width: 1100px;
}

.sheet-row,
.inline-row,
.output-mode-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.field-label {
  font-size: 13px;
  color: #8a94a6;
  white-space: nowrap;
}

.section-hint,
.empty-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #8a94a6;
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.item-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #e8ecf1;
    border-radius: 8px;
    margin-bottom: 8px;
    background: #fafbfc;
  }

  strong {
    font-size: 13px;
    color: #1f2937;
    margin-right: 8px;
  }
}

.badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f0f6ff;
  color: #3788ee;
  font-size: 11px;
}

.filter-item {
  align-items: flex-start !important;
}

.filter-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  flex: 1;
}

.inp {
  height: 32px;
  border: 1px solid #e8ecf1;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 13px;
  min-width: 110px;

  &.grow {
    flex: 1;
    min-width: 140px;
  }
}

.op {
  font-size: 12px;
  color: #8a94a6;
}

.kind-tools {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.tab-btn {
  border: 1px solid #e8ecf1;
  background: #fff;
  border-radius: 6px;
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
  color: #8a94a6;
  cursor: pointer;

  &.active {
    border-color: #3788ee;
    color: #3788ee;
    background: #f0f6ff;
  }
}

.kind-table-wrap {
  max-height: 340px;
  overflow: auto;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
}

.kind-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f1f5f9;
    text-align: left;
    color: #374151;
  }

  th {
    position: sticky;
    top: 0;
    background: #f7f9fc;
    z-index: 1;
    color: #8a94a6;
    font-weight: 600;
  }
}

.auto-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f7f9fc;
  color: #8a94a6;
  font-size: 12px;
}

.kind-select {
  height: 30px;
  min-width: 120px;
  border: 1px solid #e8ecf1;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13px;
  color: #374151;
  background: #fff;
}

.radio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}

.dl-link {
  margin-left: 8px;
  color: #3788ee;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
